import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, XCircle, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

// --- Status resolution ---

export type BalanceWarningStatus = 'low' | 'depleted' | 'expiring' | 'expired' | 'info';

export interface BalanceWarningInput {
  balance: number;
  warningThreshold?: number | null;
  carryForwardBalance: number;
  carryForwardExpiry?: string | null;
}

/**
 * Resolve which warning badges to show for a given balance.
 * Returns an array because a balance can be both "low" AND have carry-forward "expiring".
 */
export function resolveBalanceWarnings(input: BalanceWarningInput): BalanceWarningStatus[] {
  const warnings: BalanceWarningStatus[] = [];

  // Depleted takes precedence over low
  if (input.balance <= 0) {
    warnings.push('depleted');
  } else if (
    input.warningThreshold != null &&
    input.balance <= input.warningThreshold
  ) {
    warnings.push('low');
  }

  // Carry-forward states
  if (input.carryForwardBalance > 0 && input.carryForwardExpiry) {
    const expiryDate = new Date(input.carryForwardExpiry);
    const daysUntilExpiry = Math.ceil(
      (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiry < 0) {
      warnings.push('expired');
    } else if (daysUntilExpiry <= 30) {
      warnings.push('expiring');
    } else {
      warnings.push('info');
    }
  }

  return warnings;
}

// --- CVA variants ---

const badgeVariants = cva('mt-4 p-3 rounded-lg border-s-[3px] bg-white/70', {
  variants: {
    status: {
      low: 'border-s-orange-500',
      depleted: 'border-s-red-500',
      expiring: 'border-s-orange-500',
      expired: 'border-s-gray-400',
      info: 'border-s-blue-500',
    },
  },
  defaultVariants: {
    status: 'low',
  },
});

const iconColorVariants = cva('shrink-0', {
  variants: {
    status: {
      low: 'text-black',
      depleted: 'text-black',
      expiring: 'text-black',
      expired: 'text-black line-through',
      info: 'text-black',
    },
  },
  defaultVariants: {
    status: 'low',
  },
});

const textColorVariants = cva('text-sm font-medium', {
  variants: {
    status: {
      low: 'text-black',
      depleted: 'text-black',
      expiring: 'text-black',
      expired: 'text-black line-through',
      info: 'text-black',
    },
  },
  defaultVariants: {
    status: 'low',
  },
});

const hintColorVariants = cva('text-xs mt-1', {
  variants: {
    status: {
      low: 'text-black',
      depleted: 'text-black',
      expiring: 'text-black',
      expired: 'text-black',
      info: '',
    },
  },
  defaultVariants: {
    status: 'low',
  },
});

// --- Icon mapping ---

function StatusIcon({ status, size }: { status: BalanceWarningStatus; size: number }) {
  switch (status) {
    case 'low':
      return <AlertTriangle size={size} />;
    case 'depleted':
      return <XCircle size={size} />;
    case 'expiring':
    case 'expired':
    case 'info':
      return <Clock size={size} />;
  }
}

// --- Props ---

export interface BalanceWarningBadgeProps
  extends VariantProps<typeof badgeVariants> {
  /** Warning status to display */
  status: BalanceWarningStatus;
  /** Current balance value */
  balance?: number;
  /** Carry-forward days (for expiring/expired/info) */
  carryForwardDays?: number;
  /** Expiry date string (for expiring/expired/info) */
  expiryDate?: string | null;
  className?: string;
}

// --- Test ID mapping ---

const testIdMap: Record<BalanceWarningStatus, string> = {
  low: 'balance-warning-badge',
  depleted: 'balance-depleted-badge',
  expiring: 'carry-forward-expiry-warning',
  expired: 'carry-forward-expired',
  info: 'carry-forward-info',
};

// --- Component ---

export function BalanceWarningBadge({
  status,
  balance,
  carryForwardDays,
  expiryDate,
  className,
}: BalanceWarningBadgeProps) {
  const { t } = useTranslation('leaves');

  const parsedExpiry = expiryDate ? new Date(expiryDate) : null;
  const daysUntilExpiry = parsedExpiry
    ? Math.ceil((parsedExpiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const { label, hint } = getContent(status, {
    t,
    balance,
    carryForwardDays,
    expiryDate: parsedExpiry,
    daysUntilExpiry,
  });

  return (
    <div
      className={cn(badgeVariants({ status }), className)}
      data-testid={testIdMap[status]}
    >
      <div className="flex items-center gap-2 rtl:flex-row-reverse">
        <span className={iconColorVariants({ status })}>
          <StatusIcon status={status} size={16} />
        </span>
        <span className={textColorVariants({ status })}>
          {label}
        </span>
      </div>
      {hint && (
        <p className={hintColorVariants({ status })}>
          {hint}
        </p>
      )}
    </div>
  );
}

// --- Content resolution ---

const r = (n: number) => Math.round(n * 100) / 100;

function getContent(
  status: BalanceWarningStatus,
  ctx: {
    t: (key: string, opts?: Record<string, unknown>) => string;
    balance?: number;
    carryForwardDays?: number;
    expiryDate: Date | null;
    daysUntilExpiry: number | null;
  }
): { label: string; hint: string | null } {
  const { t, balance, carryForwardDays, expiryDate, daysUntilExpiry } = ctx;

  switch (status) {
    case 'low':
      return {
        label: t('Low balance warning'),
        hint: balance != null
          ? `${r(balance)} ${balance === 1 ? t('day') : t('days')} ${t('remaining')}`
          : null,
      };

    case 'depleted':
      return {
        label: t('balanceDepleted', { defaultValue: 'Balance depleted' }),
        hint: t('noBalanceMessage'),
      };

    case 'expiring':
      return {
        label: `${r(carryForwardDays ?? 0)} ${t('days')} ${t('carryForwardExpireOn', { date: expiryDate?.toLocaleDateString() ?? '', defaultValue: 'expire on {{date}}' })}`,
        hint: daysUntilExpiry === 0
          ? t('expiresToday', { defaultValue: 'Expires today' })
          : `${daysUntilExpiry} ${daysUntilExpiry === 1 ? t('day') : t('days')} ${t('remaining')}`,
      };

    case 'expired':
      return {
        label: `${r(carryForwardDays ?? 0)} ${t('days')} ${t('carriedForward', { defaultValue: 'carried forward' })}`,
        hint: `${t('forfeitedExpiredOn', { date: expiryDate?.toLocaleDateString() ?? '', defaultValue: 'Forfeited - expired on {{date}}' })}`,
      };

    case 'info':
      return {
        label: `${r(carryForwardDays ?? 0)} ${t('days')} ${t('carryForwardExpireOn', { date: expiryDate?.toLocaleDateString() ?? '', defaultValue: 'expire on {{date}}' })}`,
        hint: null,
      };
  }
}

export default BalanceWarningBadge;
